import { Category } from "@common/types/category";
import { Taxonomy, TaxonomyMainCategories } from "@common/categories";
import { FiltersTypes } from "@common/types/filters/general";
import { CategoryGlMapping } from "@common/contracts/category.contract";
import { matchesCategorySearch } from "./utils";

export enum TransactionCategoryRowType {
    Standard = "category",
    Custom = "customCategory",
}

export interface TransactionCategoryRow {
    id: string;
    parentId: string | null;
    label: string;
    description: string | null;
    type: TransactionCategoryRowType;
    category: Category;
    taxonomy: TaxonomyMainCategories;
    isEditable: boolean;
    disableAddChildren?: boolean;
    // stats (populated after data fetch)
    percentage: number;
    groupPercentage: number;
    transactionsCount: number;
    groupTransactionsCount: number;
    glMapping?: CategoryGlMapping;
    children?: TransactionCategoryRow[];
}

export interface CategoryStatMap {
    count: number;
    groupCount: number;
}

function matchesFilter(
    category: Category,
    filters: Pick<FiltersTypes, "category">,
): boolean {
    if (!filters.category?.[0]) {
        return true;
    }
    return filters.category[0] === category.id;
}

function matchesChildFilter(
    child: Category,
    filters: Pick<FiltersTypes, "category">,
): boolean {
    if (!filters.category?.[0]) {
        return true;
    }
    return filters.category[0] === child.parentCategoryId;
}

export interface BuildCategoryRowsOptions {
    search: string;
    filters: Pick<FiltersTypes, "category">;
    statsMap: Map<string, CategoryStatMap>;
    total: number;
    glMappingsMap?: Map<string, CategoryGlMapping>;
}

export function buildTransactionCategoryRows(
    categories: Category[],
    options: BuildCategoryRowsOptions,
): TransactionCategoryRow[] {
    const { search, filters, statsMap, total, glMappingsMap } = options;
    const rows: TransactionCategoryRow[] = [];

    for (const category of categories) {
        const children: TransactionCategoryRow[] = (
            category.subcategories ?? []
        )
            .filter((child) => {
                if (!matchesChildFilter(child, filters)) {
                    return false;
                }
                if (search.length === 0) {
                    return true;
                }
                return matchesCategorySearch(child, search);
            })
            .map((child): TransactionCategoryRow => ({
                id: child.id,
                parentId: category.id,
                label: child.label,
                description: child.description ?? null,
                type: child.workspaceId
                    ? TransactionCategoryRowType.Custom
                    : TransactionCategoryRowType.Standard,
                category: child,
                taxonomy: category.taxonomy as TaxonomyMainCategories,
                isEditable: Boolean(child.workspaceId),
                percentage:
                    total === 0
                        ? 0
                        : ((statsMap.get(child.id)?.count ?? 0) / total) * 100,
                groupPercentage:
                    total === 0
                        ? 0
                        : ((statsMap.get(child.id)?.groupCount ?? 0) / total) *
                          100,
                transactionsCount: statsMap.get(child.id)?.count ?? 0,
                groupTransactionsCount: statsMap.get(child.id)?.groupCount ?? 0,
                glMapping: glMappingsMap?.get(child.id),
                children: [],
            }));

        const parentMatchesSearch =
            search.length === 0 ||
            matchesCategorySearch(category, search) ||
            children.length > 0;

        const parentMatchesFilter = matchesFilter(category, filters);

        if (!parentMatchesSearch || !parentMatchesFilter) {
            continue;
        }

        rows.push({
            id: category.id,
            parentId: category.parentCategoryId,
            label: category.label,
            description: category.description ?? null,
            type: TransactionCategoryRowType.Standard,
            category,
            taxonomy: category.taxonomy as TaxonomyMainCategories,
            isEditable: false,
            disableAddChildren: category.taxonomy === Taxonomy.uncategorized,
            percentage:
                total === 0
                    ? 0
                    : ((statsMap.get(category.id)?.count ?? 0) / total) * 100,
            groupPercentage:
                total === 0
                    ? 0
                    : ((statsMap.get(category.id)?.groupCount ?? 0) / total) *
                      100,
            transactionsCount: statsMap.get(category.id)?.count ?? 0,
            groupTransactionsCount: statsMap.get(category.id)?.groupCount ?? 0,
            glMapping: glMappingsMap?.get(category.id),
            children,
        });
    }

    return rows;
}

export { getDefaultExpandedState as getCategoryDefaultExpandedState } from "@design-system/ViewTable/core/getDefaultExpandedState";
